import type {
  IHeatmapFilterPayload,
  IHeatmapBaseFilterPayload,
  EVisitorType,
  ETrafficSourceType,
  EPurchaseType,
  EVisitorJourneyOperator,
  IFilterUrlPath,
} from '../types';
import type { EAnalyticMode } from '~/modules/analytics/types';

import { detectShopifyPageType } from '~/utils/detectShopifyPageType';
import { EHeatmapFilterType, EHeatmapBaseFilterType } from '../types';
import { useSearchParams } from '@remix-run/react';
import { useCallback } from 'react';

// ===========================
// Serializer Config Type
// ===========================

interface ISearchParamSerializer<T> {
  build: (value: T) => string;
  parse: (raw: string) => T | undefined;
}

// ===========================
// Filter Serializers
// ===========================

type FilterSerializerMap = {
  [K in EHeatmapFilterType]: ISearchParamSerializer<NonNullable<IHeatmapFilterPayload[K]>>;
};

const filterSerializers: FilterSerializerMap = {
  [EHeatmapFilterType.VISITOR_JOURNEY]: {
    build: (filter) => JSON.stringify(filter.value),
    parse: (raw) => {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? { value: parsed } : undefined;
      } catch {
        return undefined;
      }
    },
  },

  [EHeatmapFilterType.VISITOR_TYPE]: {
    build: (filter) => (filter.value ?? []).join(','),
    parse: (raw) => {
      const values = raw.split(',').filter(Boolean);
      return values.length > 0 ? { value: values as EVisitorType[] } : undefined;
    },
  },

  [EHeatmapFilterType.TRAFFIC_SOURCE]: {
    build: (filter) => (filter.value ?? []).join(','),
    parse: (raw) => {
      const values = raw.split(',').filter(Boolean);
      return values.length > 0 ? { value: values as ETrafficSourceType[] } : undefined;
    },
  },

  [EHeatmapFilterType.PURCHASE_TYPE]: {
    build: (filter) => filter.value ?? '',
    parse: (raw) => (raw ? { value: raw as EPurchaseType } : undefined),
  },

  [EHeatmapFilterType.TIME_ON_PAGE]: {
    build: (filter) => (filter.value ?? []).join(','),
    parse: (raw) => {
      const [min, max] = raw.split(',');
      return min !== undefined && max !== undefined ? { value: [min, max] } : undefined;
    },
  },

  [EHeatmapFilterType.ORDER_VALUE]: {
    build: (filter) => (filter.value ?? []).join(','),
    parse: (raw) => {
      const [min, max] = raw.split(',');
      return min !== undefined && max !== undefined ? { value: [min, max] } : undefined;
    },
  },

  [EHeatmapFilterType.CAMPAIGN]: {
    build: (filter) => JSON.stringify(filter.value),
    parse: (raw) => {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? { value: parsed } : undefined;
      } catch {
        return undefined;
      }
    },
  },

  [EHeatmapFilterType.REFERRAL_SOURCE]: {
    build: (filter) => JSON.stringify({ operator: filter.operator, value: filter.value }),
    parse: (raw) => {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object'
          ? { operator: parsed.operator, value: parsed.value }
          : undefined;
      } catch {
        return undefined;
      }
    },
  },

  [EHeatmapFilterType.MARKET]: {
    build: (filter) => (filter.value ?? []).join(','),
    parse: (raw) => {
      const values = raw.split(',').filter(Boolean);
      return values.length > 0 ? { value: values } : undefined;
    },
  },
};

type BaseFilterSerializerMap = {
  [K in EHeatmapBaseFilterType]: ISearchParamSerializer<NonNullable<IHeatmapBaseFilterPayload[K]>>;
};

const baseFilterSerializers: BaseFilterSerializerMap = {
  [EHeatmapBaseFilterType.DATE_RANGE]: {
    build: (filter) => JSON.stringify(filter.value),
    parse: (raw) => {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? { value: parsed } : undefined;
      } catch {
        return undefined;
      }
    },
  },

  [EHeatmapBaseFilterType.URL_PATH]: {
    build: (filter) => JSON.stringify(filter.value),
    parse: (raw) => {
      try {
        const parsed: IFilterUrlPath['value'] = JSON.parse(raw);
        const urlPath = parsed?.path;
        if (!urlPath) return undefined;

        const pageType = parsed?.pageType ?? detectShopifyPageType(urlPath);
        return { value: { path: urlPath, pageType } };
      } catch {
        return undefined;
      }
    },
  },

  [EHeatmapBaseFilterType.ANALYTIC_MODE]: {
    build: (filter) => filter.value ?? '',
    parse: (raw) => (raw ? { value: raw as EAnalyticMode } : undefined),
  },

  [EHeatmapBaseFilterType.OPERATOR]: {
    build: (filter) => filter.value ?? '',
    parse: (raw) => (raw ? { value: raw as EVisitorJourneyOperator } : undefined),
  },
};

export function useHeatmapFilterBuildParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const buildSearchParams = useCallback(
    (filters: Partial<IHeatmapFilterPayload>, baseFilters?: Partial<IHeatmapBaseFilterPayload>) => {
      const params = new URLSearchParams();

      // Build filter search params
      for (const key of Object.values(EHeatmapFilterType)) {
        const filterValue = filters[key];
        if (filterValue) {
          const serializer = filterSerializers[key];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          params.set(key, serializer.build(filterValue as any));
        } else {
          params.delete(key);
        }
      }

      // Build base filter search params
      if (baseFilters) {
        for (const key of Object.values(EHeatmapBaseFilterType)) {
          const filterValue = baseFilters[key];
          if (filterValue) {
            const serializer = baseFilterSerializers[key];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params.set(key, serializer.build(filterValue as any));
          } else {
            params.delete(key);
          }
        }
      }

      return params;
    },
    [],
  );

  const parseSearchParams = useCallback((): {
    filters: Partial<IHeatmapFilterPayload>;
    baseFilters: Partial<IHeatmapBaseFilterPayload>;
  } => {
    const filters: Partial<IHeatmapFilterPayload> = {};
    const baseFilters: Partial<IHeatmapBaseFilterPayload> = {};

    for (const key of Object.values(EHeatmapFilterType)) {
      const raw = searchParams.get(key);
      if (!raw) continue;
      const serializer = filterSerializers[key];
      const parsed = serializer.parse(raw);
      if (parsed) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (filters as any)[key] = parsed;
      }
    }

    for (const key of Object.values(EHeatmapBaseFilterType)) {
      const raw = searchParams.get(key);
      if (!raw) continue;
      const serializer = baseFilterSerializers[key];
      const parsed = serializer.parse(raw);
      if (parsed) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (baseFilters as any)[key] = parsed;
      }
    }

    return { filters, baseFilters };
  }, [searchParams]);

  const applySearchParams = useCallback(
    (filters: Partial<IHeatmapFilterPayload>, baseFilters?: Partial<IHeatmapBaseFilterPayload>) => {
      const params = buildSearchParams(filters, baseFilters);
      setSearchParams(params, { replace: true });
    },
    [buildSearchParams, setSearchParams],
  );

  return {
    buildSearchParams,
    parseSearchParams,
    applySearchParams,
  };
}
